<template>
  <div>
    <div class="admin-toolbar">
      <p class="admin-subtitle">
        {{ contacts.length }} {{ contacts.length === 1 ? 'message' : 'messages' }} from the Contact Us form
      </p>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="admin-empty">Loading messages...</div>

    <!-- Error -->
    <div v-else-if="error" class="admin-empty text-red-600">{{ error }}</div>

    <!-- No Data -->
    <div v-else-if="contacts.length === 0" class="admin-empty">
      <i class="fa-solid fa-envelope-open text-2xl text-gray-300"></i>
      No messages yet.
    </div>

    <!-- Table -->
    <div v-else class="admin-table-wrap">
      <table class="admin-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>From</th>
            <th>Message</th>
            <th class="text-right">Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="contact in sortedContacts" :key="contact.id">
            <td class="whitespace-nowrap text-gray-500">{{ formatDate(contact.created_at) }}</td>
            <td class="whitespace-nowrap">
              <p class="font-medium text-gray-900">{{ contact.name || 'N/A' }}</p>
              <a v-if="contact.email" :href="`mailto:${contact.email}`" class="block text-xs text-brand-red hover:underline">{{ contact.email }}</a>
              <a v-if="contact.contact" :href="`tel:${contact.contact}`" class="block text-xs tabular-nums text-gray-500 hover:underline">{{ contact.contact }}</a>
            </td>
            <td class="max-w-lg">
              <p class="whitespace-pre-wrap">{{ isExpanded(contact.id) ? contact.message : truncateMessage(contact.message) }}</p>
              <button
                v-if="contact.message && contact.message.length > 140"
                @click="toggle(contact.id)"
                class="mt-1 text-xs font-semibold text-brand-red hover:underline"
              >
                {{ isExpanded(contact.id) ? 'Show less' : 'Read full message' }}
              </button>
            </td>
            <td class="text-right">
              <button @click="deleteContact(contact.id)" class="btn-danger btn-sm">Delete</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import Swal from 'sweetalert2'
import api, { errorMessage } from '@/api'

const contacts = ref([])
const loading = ref(false)
const error = ref(null)
const expanded = ref(new Set())

// Newest first
const sortedContacts = computed(() =>
  [...contacts.value].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
)

// Fetch contacts
const fetchContacts = async () => {
  loading.value = true
  error.value = null
  try {
    const response = await api.get('/contacts')
    contacts.value = response.data
  } catch (err) {
    error.value = errorMessage(err, 'Failed to fetch messages')
  } finally {
    loading.value = false
  }
}

// Delete contact
const deleteContact = async (id) => {
  const result = await Swal.fire({
    title: 'Delete Message?',
    text: "Are you sure you want to delete this message?",
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#DC2626',
    cancelButtonColor: '#6B7280',
    confirmButtonText: 'Yes, delete it!'
  });

  if (!result.isConfirmed) return;

  try {
    await api.delete(`/contacts/${id}`)
    contacts.value = contacts.value.filter(c => c.id !== id)
    Swal.fire({ icon: 'success', title: 'Deleted!', text: 'Message deleted.', timer: 1600, showConfirmButton: false });
  } catch (err) {
    Swal.fire({ icon: 'error', title: 'Error', text: errorMessage(err, 'Failed to delete message'), confirmButtonColor: '#BF3131' });
  }
}

const isExpanded = (id) => expanded.value.has(id)
const toggle = (id) => {
  const next = new Set(expanded.value)
  next.has(id) ? next.delete(id) : next.add(id)
  expanded.value = next
}

// Helper to limit message length
const truncateMessage = (msg) => {
  if (!msg) return 'N/A'
  return msg.length > 140 ? msg.substring(0, 140) + '...' : msg
}

const formatDate = (value) => {
  if (!value) return 'N/A'
  return new Date(value).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

onMounted(fetchContacts)
</script>
